import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MarketNews = () => {
  const [dollar, setDollar] = useState(null);
  const [news, setNews] = useState([]);
  const [tips, setTips] = useState({ static_tips: [], ai_tips: [], portfolio_summary: {} });
  const [loadingDollar, setLoadingDollar] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingTips, setLoadingTips] = useState(true);

  useEffect(() => {
    fetchDollar();
    fetchNews();
    fetchTips();
  }, []);

  const fetchDollar = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/market/dollar`);
      setDollar(res.data);
    } catch (error) {
      console.error('Erro ao buscar cotações:', error);
    } finally {
      setLoadingDollar(false);
    }
  };

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/market/news`);
      setNews(res.data.articles || []);
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
    } finally {
      setLoadingNews(false);
    }
  };

  const fetchTips = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/market/tips`, { withCredentials: true });
      setTips(res.data);
    } catch (error) {
      console.error('Erro ao buscar dicas:', error);
    } finally {
      setLoadingTips(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const CurrencyCard = ({ data, label, icon }) => {
    if (!data) return null;
    const isPositive = data.pct_change >= 0;
    
    return (
      <Card className="overflow-hidden">
        <div className={`h-1 ${isPositive ? 'bg-green-500' : 'bg-red-500'}`} />
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{icon}</span>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{label}</p>
                <p className="text-2xl font-bold">{formatCurrency(data.bid)}</p>
              </div>
            </div>
            <div className={`text-right`}>
              <Badge className={`${isPositive ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}`}>
                {isPositive ? '▲' : '▼'} {Math.abs(data.pct_change).toFixed(2)}%
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                Var: {isPositive ? '+' : ''}{data.variation.toFixed(4)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t">
            <div>
              <p className="text-xs text-muted-foreground">Mínima</p>
              <p className="text-sm font-semibold">{formatCurrency(data.low)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Máxima</p>
              <p className="text-sm font-semibold">{formatCurrency(data.high)}</p>
            </div>
          </div>
          {data.timestamp && (
            <p className="text-xs text-muted-foreground mt-2">
              Atualizado: {new Date(data.timestamp).toLocaleString('pt-BR')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  const SkeletonCard = () => (
    <Card className="overflow-hidden animate-pulse">
      <div className="h-1 bg-gray-200" />
      <CardContent className="p-4">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8 mt-10 pt-8 border-t-2 border-dashed">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <span className="text-xl">📰</span>
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Mercado Financeiro</h2>
          <p className="text-muted-foreground">Cotações, notícias e dicas de investimento</p>
        </div>
      </div>

      {/* Currency Rates */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <span>💱</span> Cotações em Tempo Real
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loadingDollar ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              <CurrencyCard data={dollar?.usd_brl} label="Dólar Americano (USD)" icon="🇺🇸" />
              <CurrencyCard data={dollar?.eur_brl} label="Euro (EUR)" icon="🇪🇺" />
            </>
          )}
        </div>
      </div>

      {/* Financial News */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <span>📰</span> Notícias do Mercado
        </h3>
        {loadingNews ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-full mb-1" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : news.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {news.slice(0, 6).map((article, index) => (
              <Card 
                key={index} 
                className="hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => article.url && window.open(article.url, '_blank')}
              >
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    {article.image && (
                      <img 
                        src={article.image} 
                        alt="" 
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </h4>
                      {article.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {article.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {article.source && (
                          <Badge variant="outline" className="text-xs">
                            {article.source}
                          </Badge>
                        )}
                        {article.published_at && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(article.published_at).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <p>Nenhuma notícia disponível no momento</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Investment Tips */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <span>💡</span> Dicas de Investimento
        </h3>
        
        {loadingTips ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-8 w-8 bg-gray-200 rounded-full mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-full mb-1" />
                  <div className="h-3 bg-gray-200 rounded w-4/5" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* AI-Personalized Tips */}
            {tips.ai_tips && tips.ai_tips.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                    🤖 Personalizado por IA
                  </Badge>
                  <span className="text-xs text-muted-foreground">Baseado no seu perfil financeiro</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tips.ai_tips.map((tip, index) => (
                    <Card key={`ai-${index}`} className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-transparent">
                      <CardContent className="p-4">
                        <div className="text-3xl mb-2">{tip.icon}</div>
                        <h4 className="font-semibold text-sm mb-1">{tip.title}</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{tip.tip}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Static Tips */}
            {tips.static_tips && tips.static_tips.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                    📚 Boas Práticas
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tips.static_tips.map((tip, index) => (
                    <Card key={`static-${index}`} className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-transparent">
                      <CardContent className="p-4">
                        <div className="text-3xl mb-2">{tip.icon}</div>
                        <h4 className="font-semibold text-sm mb-1">{tip.title}</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{tip.tip}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio Summary */}
            {tips.portfolio_summary && tips.portfolio_summary.total_invested > 0 && (
              <Card className="bg-gradient-to-r from-primary/5 to-transparent border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">📊</span>
                    <h4 className="font-semibold text-sm">Resumo do seu Portfólio</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Investido</p>
                      <p className="font-bold text-primary">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(tips.portfolio_summary.total_invested)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Saldo Disponível</p>
                      <p className="font-bold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(tips.portfolio_summary.balance)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Investimentos</p>
                      <p className="font-bold">{tips.portfolio_summary.investment_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Tipos</p>
                      <p className="font-bold">{Object.keys(tips.portfolio_summary.investment_types || {}).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketNews;
